/**
 * iter346 — Rappel de complétude au démarrage des travaux.
 *
 * S'ouvre quand `PATCH /or/{id}/statut` → `commencement` renvoie 409
 * `completude_incomplete`. Liste ce qui manque, permet de compléter sur place
 * (OR, fiche client ou N° de sinistre) et, pour le reste, de confirmer le
 * démarrage en l'état. La confirmation est tracée sur l'OR.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, AlertTriangle, Play } from "lucide-react";

export default function OrCompletudeDialog({ open, manquants = [], onClose, onConfirm }) {
  const [valeurs, setValeurs] = useState({});
  const [accepte, setAccepte] = useState(false);
  const [busy, setBusy] = useState(false);

  const remplis = manquants.filter((m) => (valeurs[m.key] ?? "").toString().trim() !== "");
  const restants = manquants.filter(
    (m) => (valeurs[m.key] ?? "").toString().trim() === "",
  );

  const valider = async () => {
    setBusy(true);
    try {
      await onConfirm({
        valeurs: Object.fromEntries(remplis.map((m) => [m.key, valeurs[m.key].toString().trim()])),
        manquants,
      });
      setValeurs({});
      setAccepte(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
      <DialogContent className="max-w-xl" data-testid="or-completude-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Informations manquantes sur cet OR
          </DialogTitle>
          <DialogDescription>
            Vous pouvez compléter ici avant de lancer les travaux, ou démarrer quand même :
            rien n&apos;est bloquant, mais ce qui reste vide est enregistré comme tel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[50vh] overflow-auto pr-1">
          {manquants.map((m) => (
            <div key={m.key} data-testid={`completude-item-${m.key}`}>
              <Label className="text-sm">{m.label}</Label>
              {m.aide && <p className="text-xs text-muted-foreground mb-1">{m.aide}</p>}
              {m.type === "textarea" ? (
                <Textarea
                  rows={2}
                  value={valeurs[m.key] ?? ""}
                  onChange={(e) => setValeurs((v) => ({ ...v, [m.key]: e.target.value }))}
                  data-testid={`completude-input-${m.key}`}
                />
              ) : m.type ? (
                <div className="flex items-center gap-2">
                  <Input
                    type={m.type}
                    value={valeurs[m.key] ?? ""}
                    onChange={(e) => setValeurs((v) => ({ ...v, [m.key]: e.target.value }))}
                    data-testid={`completude-input-${m.key}`}
                  />
                  {m.valeur_proposee && (
                    <Button
                      type="button" variant="outline" size="sm" className="text-xs flex-shrink-0"
                      onClick={() => setValeurs((v) => ({ ...v, [m.key]: m.valeur_proposee }))}
                      data-testid={`completude-reprendre-${m.key}`}
                    >
                      Reprendre {m.valeur_proposee}
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-amber-700">À compléter depuis l&apos;OR</p>
              )}
            </div>
          ))}
        </div>

        <label className="flex items-start gap-2 text-sm cursor-pointer bg-amber-50 border border-amber-200 rounded px-3 py-2">
          <input
            type="checkbox"
            checked={accepte}
            onChange={(e) => setAccepte(e.target.checked)}
            className="mt-0.5"
            data-testid="completude-accepte"
          />
          <span>
            Je démarre les travaux en laissant {restants.length || "ces"} point(s) non complété(s)
            {restants.length > 0 && (
              <span className="text-amber-800"> : {restants.map((m) => m.label).join(", ")}</span>
            )}
          </span>
        </label>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="completude-cancel">
            Annuler
          </Button>
          <Button
            onClick={valider}
            disabled={busy || (restants.length > 0 && !accepte)}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="completude-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            {remplis.length > 0 ? `Enregistrer et démarrer` : "Démarrer les travaux"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
